/**
 * Página de Verificación de Email
 * - Si llega con ?oobCode= (desde el link del email): aplica el código y redirige automáticamente
 * - Si no: muestra instrucciones para revisar el correo
 */

import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isSafeRedirect } from "@/lib/utils";
import {
  Mail,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Sparkles,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";
import { auth } from "@/lib/firebase";
import { applyActionCode, onAuthStateChanged } from "firebase/auth";

interface VerifyEmailPageProps {
  email?: string;
  redirect?: string;
}

const RESEND_COOLDOWN = 60; // segundos

export default function VerifyEmailPage({
  email,
  redirect,
}: VerifyEmailPageProps) {
  const { resendVerificationEmail } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const appliedRef = useRef(false);

  // Leer destino final: prop > URL param > sessionStorage > localStorage > /dashboard
  // URL param: cuando el usuario abre el link en pestaña nueva
  // localStorage: respaldo cross-tab por si ni URL ni sessionStorage tienen el código
  const inviteFromUrl = searchParams.get("invite");
  const pendingInvite =
    inviteFromUrl ||
    sessionStorage.getItem("pending_invite") ||
    localStorage.getItem("pending_invite");
  const rawRedirectTarget =
    redirect ||
    (pendingInvite ? `/setup?invite=${pendingInvite}` : null) ||
    "/dashboard";
  // Sanitizar: solo permitir rutas internas para prevenir Open Redirect
  const redirectTarget = isSafeRedirect(rawRedirectTarget)
    ? rawRedirectTarget
    : "/dashboard";

  // Caso: llegamos desde el link del email (?oobCode= + ?mode=verifyEmail)
  const oobCode = searchParams.get("oobCode");
  const mode = searchParams.get("mode");
  const [autoVerifying, setAutoVerifying] = useState(
    !!(oobCode && mode === "verifyEmail"),
  );
  const [autoVerified, setAutoVerified] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // Caso: llegamos desde el continueUrl de Firebase (ya verificó en su servidor, no hay oobCode)
  // Verificar silenciosamente si el email ya está marcado como verificado
  const [checkingOnMount, setCheckingOnMount] = useState(!oobCode);

  useEffect(() => {
    if (oobCode && mode === "verifyEmail") return; // manejado en el otro useEffect

    // Esperar a que Firebase Auth restaure la sesión (puede ser async al llegar por redirect)
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe(); // solo necesitamos la primera emisión
      if (!user) {
        setCheckingOnMount(false);
        return;
      }
      try {
        await user.reload();
        if (auth.currentUser?.emailVerified) {
          await auth.currentUser!.getIdToken(true);
          sessionStorage.removeItem("auth_redirect");
          sessionStorage.removeItem("pending_invite");
          localStorage.removeItem("pending_invite");
          setAutoVerified(true);
          setCheckingOnMount(false);
          let secs = 3;
          setCountdown(secs);
          countdownRef.current = setInterval(() => {
            secs--;
            setCountdown(secs);
            if (secs <= 0) {
              if (countdownRef.current) clearInterval(countdownRef.current);
              window.location.href = redirectTarget;
            }
          }, 1000);
        } else {
          setCheckingOnMount(false);
        }
      } catch {
        setCheckingOnMount(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-verificación al llegar desde el link del email
  useEffect(() => {
    if (!oobCode || mode !== "verifyEmail") return;
    if (appliedRef.current) return;
    appliedRef.current = true;

    setAutoVerifying(true);
    applyActionCode(auth, oobCode)
      .then(async () => {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
        }
        setAutoVerified(true);
        setAutoVerifying(false);
        sessionStorage.removeItem("auth_redirect");
        sessionStorage.removeItem("pending_invite");
        localStorage.removeItem("pending_invite");
        // Countdown de 3 segundos antes del redirect
        let secs = 3;
        setCountdown(secs);
        countdownRef.current = setInterval(() => {
          secs--;
          setCountdown(secs);
          if (secs <= 0) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            window.location.href = redirectTarget;
          }
        }, 1000);
      })
      .catch((err) => {
        console.error("Error applying action code:", err);
        if (err.code === "auth/invalid-action-code") {
          setError("Este enlace ya fue usado o expiró. Solicita uno nuevo.");
        } else {
          setError("Error al verificar. Intenta de nuevo.");
        }
        setAutoVerifying(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oobCode, mode]);

  // Limpiar countdown al desmontar
  useEffect(
    () => () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    },
    [],
  );

  const startResendCooldown = () => {
    setResendCountdown(RESEND_COOLDOWN);
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendEmail = async () => {
    if (!auth.currentUser) {
      setError("Sesión expirada. Vuelve a iniciar sesión.");
      return;
    }
    setResending(true);
    setError(null);
    try {
      await resendVerificationEmail();
      setResent(true);
      startResendCooldown();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error al reenviar. Intenta de nuevo.",
      );
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("Sesión expirada. Vuelve a iniciar sesión.");
      return;
    }
    setCheckingVerification(true);
    setError(null);
    try {
      await currentUser.reload();
      if (currentUser.emailVerified) {
        await currentUser.getIdToken(true);
        sessionStorage.removeItem("auth_redirect");
        sessionStorage.removeItem("pending_invite");
        localStorage.removeItem("pending_invite");
        window.location.href = redirectTarget;
      } else {
        setError(
          "Tu email aún no ha sido verificado. Revisa tu bandeja de entrada y haz clic en el enlace.",
        );
      }
    } catch {
      setError("Error al verificar. Intenta de nuevo.");
    } finally {
      setCheckingVerification(false);
    }
  };

  // ── Pantalla: verificando desde el link del email ──
  if (checkingOnMount || autoVerifying) {
    return (
      <div className="text-center py-4 space-y-6">
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary-500 to-accent-600 flex items-center justify-center shadow-xl shadow-primary-500/30">
              <Loader2 className="w-12 h-12 text-white animate-spin" />
            </div>
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-400 to-accent-500 animate-ping opacity-20" />
          </div>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Confirmando tu cuenta
          </h2>
          <p className="text-gray-500 text-sm">
            Un momento, estamos verificando tu correo...
          </p>
        </div>
      </div>
    );
  }

  // ── Pantalla: verificación exitosa (auto-redirect en curso) ──
  if (autoVerified) {
    return (
      <div className="text-center py-2 space-y-6">
        {/* Ícono animado con anillos */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-xl shadow-green-500/30">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <div className="absolute -inset-2 rounded-[28px] border-2 border-green-400/40 animate-ping" />
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Email verificado!
          </h2>
          <p className="text-gray-500 text-sm">
            Tu cuenta está activa y lista para usar.
          </p>
        </div>

        {/* Countdown bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            Redirigiendo en {countdown}s...
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-1.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-1000"
              style={{ width: `${((3 - countdown) / 3) * 100}%` }}
            />
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  // ── Pantalla normal: esperar verificación del usuario ──
  return (
    <div className="space-y-6">
      {/* Ícono de correo con decoraciones */}
      <div className="text-center">
        <div className="flex justify-center mb-5">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Mail className="w-10 h-10 text-white" />
            </div>
            {/* Puntos flotantes decorativos */}
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center shadow-sm">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Revisa tu correo
        </h2>
        <p className="text-gray-500 text-sm mb-3">
          Enviamos un enlace de verificación a:
        </p>
        {email && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-100 rounded-full">
            <div className="w-2 h-2 bg-primary-400 rounded-full" />
            <span className="font-semibold text-primary-800 text-sm">
              {email}
            </span>
          </div>
        )}
      </div>

      {/* Pasos */}
      <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
        {[
          { num: 1, text: "Abre tu aplicación de correo" },
          { num: 2, text: "Haz clic en el enlace del mensaje de Insignia" },
          { num: 3, text: "Serás redirigido automáticamente" },
        ].map(({ num, text }) => (
          <div key={num} className="flex items-center gap-3">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                num === 1
                  ? "bg-primary-100 text-primary-700"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              {num}
            </div>
            <span
              className={`text-sm ${num === 1 ? "text-gray-800 font-medium" : "text-gray-500"}`}
            >
              {text}
            </span>
          </div>
        ))}
      </div>

      {/* Tip: spam */}
      <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl text-left">
        <span className="text-lg">📬</span>
        <p className="text-sm text-amber-800">
          <span className="font-semibold">¿No llega en unos minutos?</span>{" "}
          Revisa tu carpeta de <span className="font-medium">spam</span> o
          correo no deseado.
        </p>
      </div>

      {/* Mensajes de estado */}
      {resent && (
        <div className="p-3 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center gap-2 text-green-700">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">
            Correo reenviado correctamente
          </span>
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Acciones */}
      <div className="space-y-3">
        <Button
          onClick={handleCheckVerification}
          disabled={checkingVerification}
          className="w-full py-3 font-semibold"
        >
          {checkingVerification ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Verificando...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Ya hice clic en el enlace
            </>
          )}
        </Button>

        <Button
          variant="secondary"
          onClick={handleResendEmail}
          disabled={resending || resendCountdown > 0}
          className="w-full py-3"
        >
          {resending ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Reenviando...
            </>
          ) : resendCountdown > 0 ? (
            <>
              <Clock className="w-4 h-4 mr-2" />
              Reenviar en {resendCountdown}s
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reenviar enlace
            </>
          )}
        </Button>
      </div>

      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-primary-600 transition-colors"
        >
          Volver al inicio de sesión
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
